import * as THREE from 'three';
import {OrbitControls} from "three/addons";
import {Connection} from "@model/Connection";
import {Controller} from "@controller/Controller";
import {ViewPawn} from "@view/ViewPawn";
import {ViewTile} from "@view/ViewTile";
import {Rules} from "@model/Rules";
import {TriangleHelper} from "@view/TriangleHelper";
import {ChoiceSquare} from "@view/ChoiceSquare";
import {ModelsLoader} from "@view/ModelsLoader";

export class Dameleon {
    scene;
    camera;
    renderer;
    controls;
    canvas;
    mouse;
    raycaster;
    lightsGroup;

    screenWidth;
    screenHeight;

    controller;
    connection;

    viewPawnMap;
    viewTilesMap;
    triangleHelper;

    selectedViewPawn;
    selectedViewTiles;
    hoveredViewPawn;

    possibleTiles;

    hoveredCaseViewPawnList;

    playableTilesGroupList;
    hoveredTilesGroupList;


    isCameraAnimating;
    cameraAnimStartTime;
    cameraAnimStartRadius;
    cameraAnimTargetRadius;
    cameraAnimDuration;
    cameraAnimStartAngle;
    cameraAnimTargetAngle;
    cameraAnimStartHeight;
    cameraAnimTargetHeight;

    firstUpdate;


    //TODO: traduire en anglais
    /**
     * Mapping des couleurs binaires (1..7) vers des couleurs Three.js.
     * 1: Cyan, 2: Jaune, 4: Magenta (Primaires)
     * 3: Vert, 5: Bleu, 6: Rouge (Secondaires)
     * 7: Noir (Tertiaire)
     * @type {Record<number, THREE.Color>}
     */
    static COLOR_MAP = {
        1: new THREE.Color(0, 1, 1),
        2: new THREE.Color(1, 1, 0),
        4: new THREE.Color(1, 0, 1),
        3: new THREE.Color(0, 1, 0),
        5: new THREE.Color(0, 0, 1),
        6: new THREE.Color(1, 0, 0),
        7: new THREE.Color(0, 0, 0)
    };

    constructor() {
        this.viewTilesMap = new Map();
        this.viewPawnMap = new Map();
        this.hoveredTilesGroupList = [];

        this.hoveredViewPawn = null;
        this.possibleTiles = [];
        this.playableTilesGroupList = [];
        this.hoveredCaseViewPawnList = [];

        this.isCameraAnimating = false;
        this.cameraAnimStartTime = 0;
        this.cameraAnimStartRadius = 0;
        this.cameraAnimTargetRadius = 0;
        this.cameraAnimDuration = 1000;
        this.cameraAnimStartAngle = 0;
        this.cameraAnimTargetAngle = 0;
        this.cameraAnimStartHeight = 0;
        this.cameraAnimTargetHeight = 0;
        this.firstUpdate = true;

        this.initMenu();
        this.initThree().then(() => {
            if (__DEV__) {
                const {initDebug} = require('../debug/DebugManager');
                initDebug(this);
            }

            this.connection = new Connection();

            this.controller = new Controller('menu');
            this.controller.startApp(this, this.connection);

            this.animate();

            this.enableListeners();
        });
    }

    showDOMElements(elementsList) {
        for (const element of elementsList) {
            document.getElementById(element).classList.remove("none");
        }
    }

    removeDOMElements(elementsList) {
        for (const element of elementsList) {
            document.getElementById(element).classList.add("none");
        }
    }

    showLoading(text) {
        document.getElementById("chargement").classList.remove("none");
        document.getElementById("loading-message").innerText = text;
    }

    hideLoading() {
        document.getElementById("chargement").classList.add("none");
        document.getElementById("loading-message").innerText = "";
    }

    showPopUp(message, buttonMessage, color, callback) {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        let pastelColor = color;
        if (match) {
            const red = parseInt(match[1], 10);
            const green = parseInt(match[2], 10);
            const blue = parseInt(match[3], 10);
            const alpha = match[4] ? parseFloat(match[4]) : 1;

            const pastelRed = Math.round((red + 255) / 2);
            const pastelGreen = Math.round((green + 255) / 2);
            const pastelBlue = Math.round((blue + 255) / 2);

            pastelColor = `rgba(${pastelRed}, ${pastelGreen}, ${pastelBlue}, ${alpha})`;
        }
        const popup = document.getElementById('popup');
        const popupMessage = document.getElementById('popup-message');
        const btn = document.getElementById('popup-btn');

        this.showDOMElements(["popup"]);
        popupMessage.textContent = message;
        popup.style.backgroundColor = pastelColor;
        btn.style.backgroundColor = color;
        btn.innerText = buttonMessage;

        btn.onclick = () => {
            callback();
            this.removeDOMElements(["popup"]);
        };
    }

    getScreenSize() {
        return {
            newWidth: this.canvas.clientWidth || window.innerWidth,
            newHeight: this.canvas.clientHeight || window.innerHeight
        };
    }

    computeFov(screenWidth) {
        const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
        const lerp = (a, b, t) => a + (b - a) * t;
        const t = clamp((screenWidth - 360) / (1280 - 360), 0, 1);
        return lerp(80, 50, t);
    }

    resizeCanvas() {
        if (!this.camera || !this.renderer) return;

        const {newWidth, newHeight} = this.getScreenSize();
        this.screenWidth = newWidth;
        this.screenHeight = newHeight;

        this.camera.fov = this.computeFov(this.screenWidth);
        this.camera.aspect = this.screenWidth / this.screenHeight;
        this.camera.updateProjectionMatrix();

        let pixelRatio = window.devicePixelRatio;
        const maxArea = 3840 * 2160;
        const currentArea = (this.screenWidth * pixelRatio) * (this.screenHeight * pixelRatio);
        if (currentArea > maxArea) {
            pixelRatio = Math.sqrt(maxArea / (this.screenWidth * this.screenHeight));
        }
        this.renderer.setPixelRatio(pixelRatio);

        this.renderer.setSize(this.screenWidth, this.screenHeight);
    }

    getCaseKey(modelTile) {
        return `${modelTile.x}:${modelTile.y}`;
    }

    getPawnBaseKey(modelPawn) {
        return `${modelPawn.player}:${modelPawn.x}:${modelPawn.y}:${modelPawn.layer}`;
    }

    findMeshFromObject(object) {
        let foundObject = object;
        while (foundObject) {
            const hasPawnMesh = Array.from(this.viewPawnMap.values()).some(viewPawn => viewPawn.mesh === foundObject);
            const hasTileMesh = Array.from(this.viewTilesMap.values()).some(viewTile => viewTile.mesh === foundObject);
            if (hasPawnMesh || hasTileMesh) {
                return foundObject;
            }
            foundObject = foundObject.parent;
        }
        return null;
    }

    getViewTileFromModel(modelTile) {
        return this.viewTilesMap.get(this.getCaseKey(modelTile)) || null;
    }

    getViewPawnFromMesh(pawnMesh) {
        return Array.from(this.viewPawnMap.values()).find(viewPawn => viewPawn.mesh === pawnMesh) || null;
    }

    getViewTileFromMesh(tileMesh) {
        return Array.from(this.viewTilesMap.values()).find(viewTile => viewTile.mesh === tileMesh) || null;
    }

    reloadApp() {
        const hamburgerBtn = document.getElementById("exit-button");
        const aboutBtn = document.getElementById("about-button");
        const about = document.getElementById("about");

        if (hamburgerBtn) hamburgerBtn.classList.remove("active");
        if (aboutBtn) aboutBtn.classList.remove("active");
        if (about) {
            about.classList.remove("active");
            about.classList.add("none");
        }

        this.firstUpdate = true;

        if (this.controller) {
            this.controller.stopAnimation();
        }
        this.viewPawnMap.forEach(pawn => {
            pawn.isAnimating = false;
        });
        this.removeDOMElements([
            "no-webgl",
            "popup",
            "offlinePlay",
            "onlinePlay",
            "privatePlay",
            "privateJoinButton",
            "game-validation",
            "schemaCouleurs",
            "hudTop",
            "exit-button",
        ]);

        this.showDOMElements([
            "logo",
            "chargement",
            "gameButtons",
        ]);

        this.removePawnFromHUD();
        document.getElementById("textPionJ2").innerText = "Pions pris joueur 2"


        this.enableListeners();

        this.controller = new Controller('menu');
        this.controller.startApp(this, this.connection);

        this.removeChoiceSquares(this.hoveredTilesGroupList);
        this.removeChoiceSquares(this.playableTilesGroupList);
    }

    initMenu() {
        const hamburgerBtn = document.getElementById("exit-button");
        const aboutBtn = document.getElementById("about-button");
        const about = document.getElementById("about");

        const toggleMenu = (btn, panel) => {
            const isActive = btn.classList.contains("active");

            if (isActive) {
                btn.classList.remove("active");
                panel.classList.remove("active");

                setTimeout(() => {
                    if (!btn.classList.contains("active")) {
                        panel.classList.add("none");
                    }
                }, 300);
            } else {
                panel.classList.remove("none");

                requestAnimationFrame(() => {
                    btn.classList.add("active");
                    panel.classList.add("active");
                });
            }
        };

        hamburgerBtn.addEventListener("click", () => this.reloadApp());
        aboutBtn.addEventListener("click", () => toggleMenu(aboutBtn, about));

        const offlineButton = document.getElementById("offlineButton");
        const onlineButton = document.getElementById("onlineButton");

        const twoPlayersButton = document.getElementById("twoPlayersButton");
        const iaButton = document.getElementById("IAButton");
        const offlineBackButton = document.getElementById("offlineBackButton");

        const publicGame = document.getElementById("publicGame");
        const privateGame = document.getElementById("privateGame");
        const onlineBackButton = document.getElementById("onlineBackButton");

        const privateJoinButton = document.getElementById("privateJoinButton");
        const privateCreateButton = document.getElementById("privateCreateButton");
        const privateBackButton = document.getElementById("privateBackButton");
        const codeInput = document.getElementById("codeInput");

        this.removeDOMElements([
            "no-webgl",
            "popup",
            "offlinePlay",
            "onlinePlay",
            "privatePlay",
            "privateJoinButton",
            "hudTop",
            "schemaCouleurs",
            "exit-button",
        ]);

        this.showDOMElements([
            "logo",
            "chargement",
            "gameButtons",
            "about-button",
        ]);

        offlineButton.addEventListener("click", _ => {
            this.removeDOMElements(["gameButtons"]);
            this.showDOMElements(["offlinePlay"]);
        });
        offlineBackButton.addEventListener("click", _ => {
            this.removeDOMElements(["offlinePlay"]);
            this.showDOMElements(["gameButtons"]);
        });

        onlineButton.addEventListener("click", _ => {
            this.removeDOMElements(["gameButtons"]);
            this.showDOMElements(["onlinePlay"]);
        });
        onlineBackButton.addEventListener("click", _ => {
            this.removeDOMElements(["onlinePlay"]);
            this.showDOMElements(["gameButtons"]);
        });
        privateGame.addEventListener("click", _ => {
            this.removeDOMElements(["onlinePlay"]);
            this.showDOMElements(["privatePlay"]);
        });
        privateBackButton.addEventListener("click", _ => {
            this.removeDOMElements(["privatePlay"]);
            this.showDOMElements(["onlinePlay"]);
        });

        twoPlayersButton.addEventListener("click", _ => {
            this.firstUpdate = true;
            this.removeDOMElements([
                "offlinePlay",
                "logo"
            ]);
            this.showDOMElements([
                "schemaCouleurs",
                "hudTop",
                "exit-button",
            ]);

            this.controller = new Controller('two_players');
            this.controller.startApp(this, this.connection);
        });
        iaButton.addEventListener("click", _ => {
            this.firstUpdate = true;
            this.removeDOMElements([
                "offlinePlay",
                "logo"
            ]);
            this.showDOMElements([
                "schemaCouleurs",
                "hudTop",
                "exit-button",
            ]);

            document.getElementById("textPionJ2").innerText = "Pions pris IA"

            this.controller = new Controller('ia');
            this.controller.startApp(this, this.connection);
        });

        publicGame.addEventListener("click", _ => {
            this.firstUpdate = true;
            this.removeDOMElements([
                "onlinePlay",
                "logo"
            ]);
            this.showDOMElements([
                "schemaCouleurs",
                "hudTop",
                "exit-button",
            ]);

            this.controller = new Controller('fast');
            this.controller.startApp(this, this.connection);
        });

        const handlePrivateGame = () => {
            this.firstUpdate = true;
            this.removeDOMElements([
                "privatePlay",
                "logo"
            ]);
            this.showDOMElements([
                "schemaCouleurs",
                "hudTop",
                "exit-button",
            ]);

            this.controller = new Controller('private');
            this.controller.startApp(this, this.connection);
        }
        privateJoinButton.addEventListener("click", _ => handlePrivateGame());
        privateCreateButton.addEventListener("click", _ => handlePrivateGame());
        codeInput.addEventListener('input', event => {
            const size = event.target.value.length;
            if (size === 6) {
                this.showDOMElements(["privateJoinButton"]);
            } else if (size > 6) {
                event.target.value = event.target.value.slice(0, 6);
            } else {
                this.removeDOMElements(["privateJoinButton"]);
            }
        });
    }

    async initThree() {
        this.showLoading("Chargement du jeu en cours ...");

        this.canvas = document.getElementById('canvas');
        const {newWidth, newHeight} = this.getScreenSize();
        this.screenWidth = newWidth;
        this.screenHeight = newHeight;

        const initialFov = this.computeFov(this.screenWidth);
        this.camera = new THREE.PerspectiveCamera(initialFov, this.screenWidth / this.screenHeight, 0.1, 100);
        this.camera.position.set(0, 5, 10);
        this.camera.lookAt(0, 0, 0);
        this.camera.layers.enable(0);
        this.camera.layers.enable(1);

        this.scene = new THREE.Scene();

        const hemisphericalLight = new THREE.HemisphereLight(0xffffff, 0x666666, 2);
        this.scene.add(hemisphericalLight);

        const pointLightCorners = [
            {x: -4, z: -4},
            {x: 4, z: -4},
            {x: -4, z: 4},
            {x: 4, z: 4},
            {x: 0, z: 0},
        ];

        this.lightsGroup = new THREE.Group();
        pointLightCorners.forEach(corner => {
            const pointLight = new THREE.PointLight(0xffffff, 10);
            pointLight.position.set(corner.x, 4, corner.z);
            this.lightsGroup.add(pointLight);

            // if (__DEV__) {
            //     const lightHelper = new THREE.PointLightHelper(pointLight, 0.3);
            //     this.lightsGroup.add(lightHelper);
            // }
        });
        this.scene.add(this.lightsGroup);

        this.renderer = new THREE.WebGLRenderer({antialias: true});
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
        this.renderer.setSize(this.screenWidth, this.screenHeight);
        this.renderer.domElement.style.display = 'block';
        this.renderer.sortObjects = true;

        this.canvas.appendChild(this.renderer.domElement);

        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enablePan = false;
        this.controls.minDistance = 5;
        this.controls.maxDistance = 15;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.04;
        this.controls.maxPolarAngle = Math.PI / 2.1;
        this.controls.enableZoom = true;
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = 1;
        this.controls.update();

        this.camera.position.set(0, 7, 9);

        this.handleResize = () => this.resizeCanvas();
        window.addEventListener('resize', this.handleResize, false);

        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseClick = this.onMouseClick.bind(this);

        this.raycaster = new THREE.Raycaster();
        this.raycaster.layers.set(0);

        this.mouse = new THREE.Vector2();

        this.triangleHelper = new TriangleHelper();

        window.addEventListener('error', () => {
            this.disableListeners();
            this.showPopUp(
                "Une erreur est survenue",
                "Recharger l'application",
                'rgba(255, 0, 0, 0.8)',
                () => window.location.reload()
            );
        });

        window.addEventListener('unhandledrejection', () => {
            this.disableListeners();
            this.showPopUp(
                "Une erreur est survenue",
                "Recharger l'application",
                'rgba(255, 0, 0, 0.8)',
                () => window.location.reload()
            );
        });

        await ModelsLoader.getInstance().awaitReady();

        this.hideLoading();
    }

    enableListeners() {
        window.addEventListener('mousemove', this.onMouseMove, false);
        window.addEventListener('click', this.onMouseClick, false);

        if (this.controls) {
            this.controls.enabled = true;
        }
    }

    disableListeners() {
        if (this.onMouseMove) {
            window.removeEventListener('mousemove', this.onMouseMove, false);
        }
        if (this.onMouseClick) {
            window.removeEventListener('click', this.onMouseClick, false);
        }

        if (this.controls) {
            this.controls.enabled = false;
        }
    }


    movePossible(pawn) {
        if (!pawn || !this.controller?.model?.checkerboard) {
            return [];
        }

        this.possibleTiles = [];
        const checkerboard = this.controller.model.checkerboard;

        for (const targetTile of checkerboard.tiles) {
            const destX = targetTile.x;
            const destY = targetTile.y;

            if (Rules.isMovementDoable(checkerboard, pawn.x, pawn.y, destX, destY)) {

                const viewTile = this.getViewTileFromModel(targetTile);
                this.possibleTiles.push(viewTile);

            }
        }
    }


    createChoiceSquare(viewTileList, color, type) {
        for (const viewTile of viewTileList) {
            const boardSize = Math.sqrt(this.controller.model.checkerboard.tiles.length);

            const choiceSquare = new ChoiceSquare(color, viewTile.getX(), viewTile.getY(), boardSize, type);

            this.scene.add(choiceSquare.group);

            switch (type) {
                case "pion":
                    this.hoveredCaseViewPawnList.push(choiceSquare);
                    break;
                case "cases":
                    this.hoveredTilesGroupList.push(choiceSquare);
                    break;
                case "movement":
                    this.playableTilesGroupList.push(choiceSquare);
                    break;
            }
        }
    }

    removeChoiceSquares(choiceSquareList) {
        for (const choiceSquare of choiceSquareList) {
            choiceSquare.dispose();
        }
    }

    addPawnToHUD(viewPawn) {
        if (viewPawn.mesh) {
            let pawn = document.createElement("div");
            let container = document.getElementById(viewPawn.getPlayer() === 1 ? "affPionsJ1" : "affPionsJ2");
            pawn.classList.add(viewPawn.getPlayer() === 1 ? "pionJ1" : "pionJ2");
            pawn.style.backgroundColor = "#" + viewPawn.getColor().getHexString();
            container.appendChild(pawn);
        }
    }

    removePawnFromHUD() {
        let containerJ1 = document.getElementById("affPionsJ1");
        let containerJ2 = document.getElementById("affPionsJ2");

        for (let i = containerJ1.children.length - 1; i >= 0; i--) {
            containerJ1.removeChild(containerJ1.children[i]);
        }
        for (let i = containerJ2.children.length - 1; i >= 0; i--) {
            containerJ2.removeChild(containerJ2.children[i]);
        }
    }


    onMouseMove(event) {

        if (this.controller.type === 'menu') return;
        if (!this.controller.isAnimationReady()) return;

        const rect = this.renderer.domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouse, this.camera);


        if (!this.selectedViewPawn) {

            const pawnMeshes = Array.from(this.viewPawnMap.values()).map(viewPawn => viewPawn.mesh);
            const intersects = this.raycaster.intersectObjects(pawnMeshes, true);

            let newHoveredViewPawn = null;
            if (intersects.length > 0) {
                const mesh = this.findMeshFromObject(intersects[0].object);
                newHoveredViewPawn = this.getViewPawnFromMesh(mesh);
            }

            if (newHoveredViewPawn !== this.hoveredViewPawn) {

                if (this.hoveredViewPawn?.model) {
                    this.removeChoiceSquares(this.hoveredCaseViewPawnList);
                }

                if (newHoveredViewPawn?.model) {
                    if (this.controller.type === "two_players") {
                        if (newHoveredViewPawn.model.player === this.controller.model.round) {
                            this.createChoiceSquare([
                                this.getViewTileFromModel(this.controller.model.checkerboard.getTile(newHoveredViewPawn.getX(),newHoveredViewPawn.getY()))
                            ], 0xffffff, "pion");
                        }
                    } else {
                        if (newHoveredViewPawn.model.player === this.controller.model.player && newHoveredViewPawn.model.player === this.controller.model.round) {
                            this.createChoiceSquare([
                                this.getViewTileFromModel(this.controller.model.checkerboard.getTile(newHoveredViewPawn.getX(),newHoveredViewPawn.getY()))
                            ], 0xffffff, "pion");
                        }
                    }
                }
                this.hoveredViewPawn = newHoveredViewPawn;
            }


        } else {
            const tileMeshes = Array.from(this.viewTilesMap.values()).map(viewTiles => viewTiles.mesh);
            const intersects = this.raycaster.intersectObjects(tileMeshes, true);

            let newHoveredViewTile = null;
            if (intersects.length > 0) {
                const mesh = this.findMeshFromObject(intersects[0].object);
                newHoveredViewTile = this.getViewTileFromMesh(mesh);
            }


            if (newHoveredViewTile !== this.selectedViewTiles) {

                if (this.selectedViewTiles) {
                    this.removeChoiceSquares(this.hoveredTilesGroupList);
                }

                if (newHoveredViewTile) {
                    this.createChoiceSquare([newHoveredViewTile], 0xffffff, "cases");
                    this.selectedViewTiles = newHoveredViewTile;
                } else {
                    this.selectedViewTiles = null;
                }
            }
        }
    }


    onMouseClick(event) {
        if (this.controller.type === 'menu') return;
        if (!this.controller.isAnimationReady()) return;
        if (this.controller.model.round !== this.controller.model.player && this.controller.type !== 'two_players') {
            return;
        }

        this.removeChoiceSquares(this.hoveredTilesGroupList);
        this.removeChoiceSquares(this.playableTilesGroupList);
        this.removeChoiceSquares(this.hoveredCaseViewPawnList);


        const rect = this.renderer.domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouse, this.camera);

        const intersects = this.raycaster.intersectObjects(this.scene.children, true);

        if (intersects.length === 0) {
            this.selectedViewPawn = null;
            return;
        }

        const object = intersects[0].object;
        const mesh = this.findMeshFromObject(object);
        if (!mesh) {
            return;
        }

        // On récupère ce qu'on peut (Case ou Pion ou les deux)
        let viewTile = this.getViewTileFromMesh(mesh);
        const viewPawn = this.getViewPawnFromMesh(mesh);

        // Si on a cliqué ni sur une case connue, ni sur un pion connu, on arrête
        if (!viewTile && !viewPawn) {
            this.removeChoiceSquares(this.playableTilesGroupList);
            return;
        }

        const localPlayer = this.controller.model.player;
        const isLocalMode = this.controller.type === 'two_players';
        const authorizedPlayer = (!isLocalMode && localPlayer != null) ? localPlayer : this.controller.model.round;

        // --- CAS 1 : SÉLECTION D'UN PION ---
        if (!this.selectedViewPawn) {
            // Pour sélectionner, il faut impérativement avoir cliqué sur un pion
            if (viewPawn) {
                const modelPawn = viewPawn.model;
                if (modelPawn && modelPawn.player === authorizedPlayer) {
                    viewPawn.selected = true;
                    this.selectedViewPawn = viewPawn;
                    this.movePossible(modelPawn);
                    this.createChoiceSquare(this.possibleTiles, 0x00ff00, "movement");
                    this.createChoiceSquare([
                        this.getViewTileFromModel(this.controller.model.checkerboard.getTile(this.selectedViewPawn.getX(),this.selectedViewPawn.getY()))
                    ], 0xffffff, "pion");
                }
            }
        }
        // --- CAS 2 : MOUVEMENT ---
        else {
            if (!this.selectedViewPawn) {
                this.selectedViewPawn = null;
                this.removeChoiceSquares(this.playableTilesGroupList);

                return;
            }

            const selectedPawn = this.selectedViewPawn.model;

            if (viewPawn && viewPawn.mesh !== this.selectedViewPawn.mesh) {
                const clickedPawn = viewPawn.model;
                if (clickedPawn && clickedPawn.player === authorizedPlayer) {

                    this.selectedViewPawn.selected = false;
                    this.removeChoiceSquares(this.playableTilesGroupList);
                    viewPawn.selected = true;
                    this.selectedViewPawn = viewPawn;
                    this.movePossible(clickedPawn);
                    this.createChoiceSquare(this.possibleTiles, 0x00ff00, "movement");
                    this.createChoiceSquare([
                        this.getViewTileFromModel(this.controller.model.checkerboard.getTile(this.selectedViewPawn.getX(),this.selectedViewPawn.getY()))
                    ], 0xffffff, "pion");

                    return;
                }
            }

            // 2. DÉTERMINATION DE LA CASE CIBLE
            let targetTile = viewTile ? viewTile.model : null;

            // Si on a cliqué sur un pion (ex: superposition ou manger), on cherche la case dessous
            if (!targetTile && viewPawn) {
                const clickedPawn = viewPawn.model;
                if (clickedPawn) {
                    targetTile = this.controller.model.checkerboard.getTile(clickedPawn.x, clickedPawn.y);
                }
            }

            // Si toujours pas de case cible valide, on annule
            if (!targetTile) {
                return;
            }

            const targetPawns = this.controller.model.checkerboard.getAllPawnsOnTile(targetTile.x, targetTile.y);

            // Si on reclique sur le même pion ou sa case actuelle, on désélectionne
            if (targetPawns.includes(selectedPawn)) {
                this.selectedViewPawn = null;
                this.removeChoiceSquares(this.playableTilesGroupList);
                this.removeChoiceSquares(this.hoveredCaseViewPawnList);

                return;
            }

            // --- PRÉPARATION ET ENVOI DU MOUVEMENT ---
            const origin = {
                x: selectedPawn.x,
                y: selectedPawn.y,
                layer: selectedPawn.layer
            };

            let targetLayer = 1;
            if (targetPawns.some(pawn => pawn.layer === 1)) targetLayer = 2;

            const captured = targetLayer === 2 ? targetPawns.filter(pawn => pawn.layer === 1) : [];

            this.pendingMove = {
                origin: origin,
                destination: {
                    x: targetTile.x,
                    y: targetTile.y,
                    layer: targetLayer
                },
                captured: captured
            };

            this.controller.controlMove(origin.x, origin.y, this.pendingMove.destination.x, this.pendingMove.destination.y, this.controller.type !== 'two_players');
            this.controller.controlEat(this.pendingMove.destination.x, this.pendingMove.destination.y);

            // Nettoyage final
            this.selectedViewPawn = null;
            this.removeChoiceSquares(this.playableTilesGroupList);
            this.removeChoiceSquares(this.hoveredTilesGroupList);
            this.removeChoiceSquares(this.hoveredCaseViewPawnList);

        }
    }

    updateView(checkerboard, player, round) {
        this.updateCheckerboard(checkerboard);
        this.updatePawns(checkerboard);
        this.updatePlayerStatus(player, round);
        this.controller.executeWhenAnimationReady(() => {
            this.updateCamera(player, round);
        });

    }

    updateCheckerboard(checkerboard) {
        const boardSize = Math.sqrt(checkerboard.tiles.length);
        const activeCaseKeys = new Set();

        checkerboard.tiles.forEach(checkerboardTile => {
            const key = this.getCaseKey(checkerboardTile);
            activeCaseKeys.add(key);

            const threeColor = Dameleon.COLOR_MAP[checkerboardTile.color];
            let viewTile = this.viewTilesMap.get(key);

            if (!viewTile) {
                viewTile = new ViewTile(this.scene, threeColor, checkerboardTile);
                viewTile.setMeshCoordinates(checkerboardTile.x - boardSize / 2 + 0.5, 0.5, checkerboardTile.y - boardSize / 2 + 0.5);
                this.viewTilesMap.set(key, viewTile);
            } else {
                viewTile.model = checkerboardTile;
                if (!viewTile.getColor().equals(threeColor)) {
                    viewTile.setColor(threeColor);
                }
            }
        });

        for (const [key, viewTile] of this.viewTilesMap.entries()) {
            if (!activeCaseKeys.has(key)) {
                viewTile.dispose();
                this.viewTilesMap.delete(key);
            }
        }
    }

    updatePawns(checkerboard) {
        const boardSize = Math.sqrt(checkerboard.tiles.length);
        const nextPawnsMap = new Map();
        const usedOldKeys = new Set();

        const getUniqueKey = (baseKey) => {
            if (!nextPawnsMap.has(baseKey)) {
                return baseKey;
            }

            let index = 1;
            while (nextPawnsMap.has(`${baseKey}#${index}`)) {
                index++;
            }
            return `${baseKey}#${index}`;
        };

        checkerboard.pawns.forEach(modelPawn => {
            const threeColorPawn = Dameleon.COLOR_MAP[modelPawn.color];
            const threeColorSide = Dameleon.COLOR_MAP[modelPawn.viewColor];

            const targetX = modelPawn.x - boardSize / 2 + 0.5;
            const targetZ = modelPawn.y - boardSize / 2 + 0.5;
            const targetY = 0.651 + (modelPawn.layer - 1) * 0.1001;

            const baseKey = this.getPawnBaseKey(modelPawn);
            let reusedKey = null;
            let viewPawn = null;

            // tentative 1, même clé logique (joueur:x:y:layer)
            if (this.viewPawnMap.has(baseKey) && !usedOldKeys.has(baseKey)) {
                reusedKey = baseKey;
                viewPawn = this.viewPawnMap.get(baseKey);
            }

            // tentative 2, même objet modèle
            if (!viewPawn) {
                for (const [oldKey, oldViewPawn] of this.viewPawnMap.entries()) {
                    if (usedOldKeys.has(oldKey)) continue;
                    if (oldViewPawn.model === modelPawn) {
                        reusedKey = oldKey;
                        viewPawn = oldViewPawn;
                        break;
                    }
                }
            }

            // tentative 3, le pion est proche de la position précédente
            if (!viewPawn) {
                let closestDistance = Number.POSITIVE_INFINITY;
                for (const [oldKey, oldViewPawn] of this.viewPawnMap.entries()) {
                    if (usedOldKeys.has(oldKey)) continue;
                    if (!oldViewPawn.model || oldViewPawn.model.player !== modelPawn.player) continue;

                    const dx = oldViewPawn.getMeshX() - targetX;
                    const dy = oldViewPawn.getMeshY() - targetY;
                    const dz = oldViewPawn.getMeshZ() - targetZ;
                    const distance = dx * dx + dy * dy + dz * dz;

                    if (distance < closestDistance) {
                        closestDistance = distance;
                        reusedKey = oldKey;
                        viewPawn = oldViewPawn;
                    }
                }
            }

            if (!viewPawn || viewPawn.getMeshGeometry() !== modelPawn.player) {
                if (viewPawn && reusedKey) {
                    usedOldKeys.add(reusedKey);
                    if (this.hoveredViewPawn === viewPawn) this.hoveredViewPawn = null;
                    if (this.selectedViewPawn === viewPawn) this.selectedViewPawn = null;
                    viewPawn.dispose();
                    viewPawn = null;
                    reusedKey = null;
                }

                viewPawn = new ViewPawn(this.scene, threeColorPawn, modelPawn);
                viewPawn.setMeshCoordinates(targetX, targetY, targetZ);
                viewPawn.setMeshRenderOrder(modelPawn.layer);
            } else {
                viewPawn.model = modelPawn;

                if (!viewPawn.getColor().equals(threeColorPawn)) {
                    viewPawn.setColor(threeColorPawn);
                }
                if (!viewPawn.getInnerEdgeColor().equals(threeColorSide)) {
                    viewPawn.setInnerEdgeColor(threeColorSide);
                }
                if (viewPawn.getMeshX() !== targetX || viewPawn.getMeshY() !== targetY || viewPawn.getMeshZ() !== targetZ) {
                    if (this.firstUpdate) {
                        viewPawn.setMeshCoordinates(targetX, targetY, targetZ);
                        viewPawn.setMeshRenderOrder(modelPawn.layer);
                    } else {
                        let shouldJump = false;

                        if (modelPawn.layer === 2) {
                            shouldJump = true;
                        } else {
                            const oldBoardX = Math.round(viewPawn.getMeshX() + boardSize / 2 - 0.5);
                            const oldBoardY = Math.round(viewPawn.getMeshZ() + boardSize / 2 - 0.5);

                            const dx = modelPawn.x - oldBoardX;
                            const dy = modelPawn.y - oldBoardY;

                            if (Math.abs(dx) === 1 && Math.abs(dy) === 1) {
                                const obstacle1 = checkerboard.getAllPawnsOnTile(oldBoardX + dx, oldBoardY).length > 0;
                                const obstacle2 = checkerboard.getAllPawnsOnTile(oldBoardX, oldBoardY + dy).length > 0;

                                if (obstacle1 || obstacle2) {
                                    shouldJump = true;
                                }
                            }
                        }

                        viewPawn.moveWithAnimation(targetX, targetY, targetZ, modelPawn.layer, shouldJump);
                    }
                }
            }

            if (reusedKey) {
                usedOldKeys.add(reusedKey);
            }

            const finalKey = getUniqueKey(baseKey);
            nextPawnsMap.set(finalKey, viewPawn);
        });

        for (const [oldKey, oldViewPawn] of this.viewPawnMap.entries()) {
            if (!usedOldKeys.has(oldKey)) {
                if (oldViewPawn.isEaten()) this.addPawnToHUD(oldViewPawn);
                if (this.hoveredViewPawn === oldViewPawn) this.hoveredViewPawn = null;
                if (this.selectedViewPawn === oldViewPawn) this.selectedViewPawn = null;
                oldViewPawn.dispose();
            }
        }

        this.viewPawnMap = nextPawnsMap;
    }

    updatePlayerStatus(player, round) {
        let playerTriangle = player;
        let displayTriangle = true;

        if (this.controller.type === 'menu') {
            displayTriangle = false;
        } else if (this.controller.type === 'two_players') {
            playerTriangle = round;
        }

        if (displayTriangle) {
            if (playerTriangle === 1) {
                this.triangleHelper.setRotation(0);
                this.triangleHelper.setPosition(-3.5);
            } else {
                this.triangleHelper.setRotation(Math.PI);
                this.triangleHelper.setPosition(3.5);
            }

            if (!this.scene.children.includes(this.triangleHelper.mesh)) {
                this.scene.add(this.triangleHelper.mesh);
            }
        } else {
            if (this.scene.children.includes(this.triangleHelper.mesh)) {
                this.scene.remove(this.triangleHelper.mesh);
            }
        }

        const playerText = document.getElementById("textJoueur");
        if (this.controller.type === 'two_players') {
            playerText.innerText = `Au tour du joueur ${round}`;
            if (player === round) {
                playerText.style.color = "#00FF00";
            } else {
                playerText.style.color = "#FF0000";
            }
        } else if (this.controller.type === 'ia') {
            if (player === round) {
                playerText.innerText = "À votre tour";
                playerText.style.color = "#00FF00";
            } else {
                playerText.innerText = "Au tour de l'IA";
                playerText.style.color = "#FF0000";
            }
        } else {
            if (player === round) {
                playerText.innerText = "À votre tour";
                playerText.style.color = "#00FF00";
            } else {
                playerText.innerText = "Au tour de l'adversaire";
                playerText.style.color = "#FF0000";
            }
        }

    }

    updateCamera(player, round) {
        if (this.controls.enabled === false) return;
        if (this.controller.type === 'menu') {
            this.controls.enableZoom = false;
            this.controls.autoRotate = true;
        } else {
            this.controls.enableZoom = true;
            this.controls.autoRotate = false;
        }
        this.controls.update();


        if (this.controller.type === 'two_players') {
            if (this.firstUpdate) {
                this.updateCameraForPlayer(round, false);
            } else {
                this.updateCameraForPlayer(round, true);
            }
        } else if (this.controller.type === 'menu') {
            if (this.firstUpdate) {
                this.updateCameraForPlayer(1, false);
            }
        } else {
            if (this.firstUpdate) {
                this.updateCameraForPlayer(player, false);
            }
        }

        if (this.firstUpdate) {
            this.firstUpdate = false;
        }
    }

    updateCameraForPlayer(player, animate = false) {
        let targetPos;
        if (player === 1) targetPos = new THREE.Vector3(0, 5, -10);
        else targetPos = new THREE.Vector3(0, 5, 10);

        if (animate) {
            this.isCameraAnimating = true;
            this.cameraAnimStartTime = performance.now();
            this.controls.enabled = false;

            const startPos = this.camera.position;
            this.cameraAnimStartRadius = Math.sqrt(startPos.x * startPos.x + startPos.z * startPos.z);
            this.cameraAnimTargetRadius = this.cameraAnimStartRadius;

            this.cameraAnimStartAngle = Math.atan2(startPos.z, startPos.x);
            this.cameraAnimTargetAngle = Math.atan2(targetPos.z, targetPos.x);

            let diff = this.cameraAnimTargetAngle - this.cameraAnimStartAngle;
            while (diff > Math.PI) diff -= 2 * Math.PI;
            while (diff < -Math.PI) diff += 2 * Math.PI;

            this.cameraAnimTargetAngle = this.cameraAnimStartAngle + diff;

            this.cameraAnimStartHeight = startPos.y;
            this.cameraAnimTargetHeight = startPos.y;

        } else {
            this.isCameraAnimating = false;
            this.camera.position.copy(targetPos);
            this.camera.lookAt(0, 0, 0);
            this.controls.target.set(0, 0, 0);
            this.controls.update();
        }
    }

    updateCameraAnimation() {
        const now = performance.now();
        let progress = (now - this.cameraAnimStartTime) / this.cameraAnimDuration;

        if (progress >= 1) {
            progress = 1;
            this.isCameraAnimating = false;
            this.controls.enabled = true;
        }

        const ease = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        const currentAngle = this.cameraAnimStartAngle + (this.cameraAnimTargetAngle - this.cameraAnimStartAngle) * ease;
        const currentRadius = this.cameraAnimStartRadius + (this.cameraAnimTargetRadius - this.cameraAnimStartRadius) * ease;
        const currentHeight = this.cameraAnimStartHeight + (this.cameraAnimTargetHeight - this.cameraAnimStartHeight) * ease;

        this.camera.position.x = currentRadius * Math.cos(currentAngle);
        this.camera.position.z = currentRadius * Math.sin(currentAngle);
        this.camera.position.y = currentHeight;

        this.camera.lookAt(0, 0, 0);
    }

    animate() {
        requestAnimationFrame(() => this.animate());

        const now = performance.now();

        if (this.isCameraAnimating) {
            this.updateCameraAnimation();
        }

        this.lightsGroup.rotation.y = Math.atan2(this.camera.position.x, this.camera.position.z) / 2;

        for (const viewPawn of this.viewPawnMap.values()) {
            viewPawn.updateAnimation(now);
        }

        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }
}

